import {
    useState,
    type ButtonHTMLAttributes,
    type ComponentType,
    type ReactNode,
    type SVGProps
} from 'react';
import { CheckIcon, ChevronDownIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { SolidStatusPill } from '@odo/shared-ui';
import type { MonitoringState } from './dmControlUtils';

export type HeroIcon = ComponentType<SVGProps<SVGSVGElement>>;

export { TOOLTIP_OVER_MODAL } from '../../../../shared/ModalShell';

const DM_INPUT_BASE =
    'block rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 disabled:bg-gray-50 disabled:text-gray-400';

export const dmInputClass = `${DM_INPUT_BASE} focus:ring-indigo-500 focus:border-indigo-500`;
export const dmTealInputClass = `${DM_INPUT_BASE} focus:ring-[#007E87] focus:border-[#007E87]`;

/** The status column's width: idle buttons and confirm steps share these edges. */
export const DM_ACTION_COLUMN = 'mx-auto w-full max-w-xs';

export function Spinner({ className = 'h-4 w-4' }: { className?: string }) {
    return (
        <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
            />
            <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
        </svg>
    );
}

const BUTTON_KIND = {
    primary: 'bg-gray-800 text-white hover:bg-gray-700 disabled:bg-gray-300',
    secondary:
        'border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 disabled:text-gray-400 disabled:hover:bg-white',
    danger: 'bg-red-600 text-white hover:bg-red-700 disabled:bg-red-200',
    dangerGhost: 'border border-gray-300 bg-white text-red-600 shadow-sm hover:bg-red-50',
    warning:
        'bg-yellow-500 text-white hover:bg-yellow-600 disabled:bg-yellow-700 disabled:text-yellow-100',
    warningGhost: 'border border-gray-300 bg-white text-yellow-700 shadow-sm hover:bg-yellow-50',
    warningOutline:
        'border border-yellow-300 bg-white text-yellow-700 shadow-sm hover:bg-yellow-50',
    // Teal, the design system's "Start monitoring" colour.
    start: 'bg-[#007E87] text-white hover:bg-[#00656C] disabled:bg-[#00565C] disabled:text-teal-100',
    startGhost: 'border border-gray-300 bg-white text-[#007E87] shadow-sm hover:bg-teal-50',
    link: 'font-medium text-[#007E87] hover:text-[#00656C] hover:underline'
} as const;

interface DmButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    kind?: keyof typeof BUTTON_KIND;
    size?: 'sm' | 'md';
    icon?: HeroIcon;
    loading?: boolean;
}

export function DmButton({
    kind = 'secondary',
    size = 'md',
    icon: Icon,
    loading,
    children,
    className = '',
    type = 'button',
    ...rest
}: DmButtonProps) {
    const padding = kind === 'link' ? '' : size === 'sm' ? 'px-3 py-1.5' : 'px-4 py-2';
    return (
        <button
            type={type}
            className={`inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed ${padding} ${BUTTON_KIND[kind]} ${className}`}
            {...rest}
        >
            {loading ? <Spinner /> : Icon && <Icon className="-ml-0.5 h-4 w-4" />}
            {children}
        </button>
    );
}

// The tones of the Actions tab's DmMonitoringStatusChip; the shared pill would hash one otherwise.
const STATUS_BADGE: Record<MonitoringState, { label: string; tone: string }> = {
    active: { label: 'Active', tone: 'bg-green-50 text-green-700' },
    paused: { label: 'Paused', tone: 'bg-yellow-50 text-yellow-700' },
    stopped: { label: 'Stopped', tone: 'bg-red-50 text-red-700' },
    not_started: { label: 'Not started', tone: 'bg-gray-100 text-gray-500' }
};

export function StatusBadge({ state }: { state: MonitoringState }) {
    const { label, tone } = STATUS_BADGE[state];
    return (
        <SolidStatusPill
            value={state}
            label={label}
            toneClassName={tone}
            dotClassName="bg-current opacity-70"
            className="shrink-0"
        />
    );
}

interface FieldProps {
    label: string;
    htmlFor?: string;
    optional?: boolean;
    className?: string;
    children: ReactNode;
}

export function Field({ label, htmlFor, optional, className = '', children }: FieldProps) {
    return (
        <div className={className}>
            <label htmlFor={htmlFor} className="mb-1 block text-xs font-medium text-gray-500">
                {label}
                {optional && <span className="font-normal text-gray-400"> · optional</span>}
            </label>
            {children}
        </div>
    );
}

interface NumberInputProps {
    id?: string;
    value: string;
    onChange: (value: string) => void;
    min: number;
    max: number;
    label?: string;
    className?: string;
}

export function NumberInput({
    id,
    value,
    onChange,
    min,
    max,
    label,
    className = 'w-14'
}: NumberInputProps) {
    return (
        <input
            id={id}
            type="number"
            min={min}
            max={max}
            value={value}
            aria-label={label}
            onChange={(e) => onChange(e.target.value)}
            className={`${dmInputClass} ${className} tabular-nums`}
        />
    );
}

/** A refusal or failure from Dental Monitoring, next to the save that hit it. */
export function DmErrorText({ message }: { message: string }) {
    return (
        <span className="flex items-start gap-1.5 text-xs text-red-500">
            <ExclamationCircleIcon className="h-4 w-4 shrink-0" />
            <span>
                <span className="font-semibold">Dental Monitoring:</span> {message}
            </span>
        </span>
    );
}

export function SavedText() {
    return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-[#007E87]">
            <CheckIcon className="h-4 w-4" />
            Saved
        </span>
    );
}

interface FoldSectionProps {
    icon: HeroIcon;
    title: string;
    /** After the title, e.g. the status or a count. */
    meta?: ReactNode;
    /** Right of the header, outside the fold toggle, e.g. an action. */
    actions?: ReactNode;
    children: ReactNode;
}

// A top-level block of the control: a titled header that folds its content away.
export function FoldSection({ icon: Icon, title, meta, actions, children }: FoldSectionProps) {
    const [open, setOpen] = useState(true);
    return (
        <section className="mt-8">
            <div className="flex items-center gap-3 border-b border-gray-200 pb-3">
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    aria-expanded={open}
                    className="group flex min-w-0 flex-1 items-center gap-2.5 text-left"
                >
                    <ChevronDownIcon
                        className={`h-4 w-4 shrink-0 text-gray-400 transition-transform group-hover:text-gray-600 ${
                            open ? '' : '-rotate-90'
                        }`}
                    />
                    <Icon className="h-5 w-5 shrink-0 text-[#007E87]" />
                    <h3 className="text-base font-semibold text-gray-800">{title}</h3>
                    {meta && (
                        <span className="flex min-w-0 flex-wrap items-center gap-2">{meta}</span>
                    )}
                </button>
                {actions && <div className="flex shrink-0 items-center gap-3">{actions}</div>}
            </div>
            {open && <div className="pt-5">{children}</div>}
        </section>
    );
}
